import '../styles/noir-pro/styles.css';
import '../styles/global.css';
import 'antd/dist/reset.css';
import '../styles/tokens.css';
import '../styles/theme/foundation.css';
import '../styles/theme/layout.css';
import '../styles/theme/ui.css';
import '../styles/theme/features.css';
import '../styles/theme/rtl.css';
import '../styles/theme/overrides.css';
import '../components/Assistant/assistant.css';

export const config = {
  maxDuration: 30,
};

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React, { useEffect, useMemo } from 'react';
import Head from 'next/head';
import type { AppProps } from 'next/app';
import Script from 'next/script';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Button, Layout, notification } from 'antd';
import moment from 'moment';
import { Analytics } from '@vercel/analytics/react';
import { useRouter } from 'next/router';

import { ProvideAuth } from 'src/util/use-auth';
import { ProvideAPI } from 'src/util/use-api';
import { queryClient } from 'src/util/query-client';
import { ProvideLayout } from 'src/util/use-layout';
import AppLayout from 'src/Layout';
import { ProvideWebsocket } from 'src/util/use-websocket';
import { BinderContextProvider } from 'src/util/context/BinderContext';
import { PermissionsContextProvider } from 'src/util/context/PermissionsContext';
import { composeWrappers } from 'src/util/composeWrappers';
import { SideBar } from 'src/components/SideBar';
import { PortfolioContextProvider } from 'src/util/context/PortfolioContext';
import { GA_TRACKING_ID, pageView } from 'src/util/gtag';
import { ProvideLocale } from 'src/util/use-locale';
import FullScreenLoader from 'src/components/Loader';
import 'src/util/i18n';

// Default all static `notification.*` toasts to the bottom-left corner, matching
// the placement used by the app's `useNotification` instances (Avatar, ReplyComposer).
notification.config({ placement: 'bottomLeft' });

const bugsnagConfig = () => ({
  apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY as string,
  appVersion: process.env.NEXT_PUBLIC_APP_VERSION || undefined,
  releaseStage: process.env.NEXT_PUBLIC_RELEASE_STAGE,
  plugins: [new BugsnagPluginReact()],
});

// @ts-ignore
let ErrorBoundary: unknown = undefined;
// @ts-ignore
if (process.env.NEXT_PUBLIC_BUGSNAG_API_KEY && !Bugsnag.isStarted()) {
  Bugsnag.start(bugsnagConfig());
  ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React);
}

const ErrorView = ({ error }: { error: Error }) => {
  const { t } = useTranslation('common');

  return (
    <Layout
      className={`gx-app-layout full-scroll horizontal-layout full-layout`}
    >
      <Layout>
        <Layout.Content className={`gx-layout-content gx-container-wrap`}>
          <div
            className="gx-main-content-wrapper"
            style={{ position: 'relative' }}
          >
            <SideBar title={t('An error occurred')}>
              <p>
                {t(
                  'A website error occurred, this error has been automatically reported. We hope to resolve this issue as soon as possible, contact support for more information.'
                )}
              </p>
              <Button
                danger
                href={`mailto:support@businessradar.com?subject=${t(
                  'Website error occurred'
                )}&body=Hi, I just experienced a front-end error. %0D %0D URL: ${
                  window.location
                } %0D %0D Error: ${error.message}`}
              >
                {t('Contact Technical Support')}
              </Button>
            </SideBar>
          </div>
          <Layout.Footer>
            <div className="gx-layout-footer-content">
              ©{moment().year()} Business Radar
            </div>
          </Layout.Footer>
        </Layout.Content>
      </Layout>
    </Layout>
  );
};

const client = queryClient;

const RootWrapper = composeWrappers([
  ({ children }) => (
    <QueryClientProvider client={client}>{children}</QueryClientProvider>
  ),
  ({ children }) => {
    if (!process.env.NEXT_PUBLIC_BUGSNAG_API_KEY) {
      return <>{children}</>;
    }

    // Create ErrorBoundary with useMemo to avoid creating a new instance on every render
    const ErrorBoundary = useMemo(() => {
      // @ts-ignore
      if (!Bugsnag.isStarted()) {
        Bugsnag.start(bugsnagConfig());
      }
      return Bugsnag.getPlugin('react')?.createErrorBoundary(React);
    }, []);

    return (
      // @ts-ignore
      <ErrorBoundary FallbackComponent={ErrorView}>{children}</ErrorBoundary>
    );
  },
  ({ children }) => <ProvideAPI>{children}</ProvideAPI>,
  ({ children }) => <ProvideAuth>{children}</ProvideAuth>,
  ({ children }) => <ProvideLocale>{children}</ProvideLocale>,
  ({ children }) => <ProvideWebsocket>{children}</ProvideWebsocket>,
  ({ children }) => <ProvideLayout>{children}</ProvideLayout>,
  ({ children }) => <BinderContextProvider>{children}</BinderContextProvider>,
  ({ children }) => (
    <PortfolioContextProvider>{children}</PortfolioContextProvider>
  ),
  ({ children }) => (
    <PermissionsContextProvider>{children}</PermissionsContextProvider>
  ),
  ({ children }) => <AppLayout>{children}</AppLayout>,
]);

const AppContent = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageView(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    // @ts-ignore
    if (process.env.NEXT_PUBLIC_BUGSNAG_API_KEY && !Bugsnag.isStarted()) {
      Bugsnag.start(bugsnagConfig());
    }
  }, []);

  return (
    <>
      <Component {...pageProps} />
    </>
  );
};

const App = ({ Component, pageProps, router }: AppProps) => {
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      pageView(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    // @ts-ignore
    if (process.env.NEXT_PUBLIC_BUGSNAG_API_KEY && !Bugsnag.isStarted()) {
      Bugsnag.start(bugsnagConfig());
    }
  }, []);

  return (
    <>
      {GA_TRACKING_ID ? (
        <Script
          id={'ga-script-import'}
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
        />
      ) : null}
      {GA_TRACKING_ID ? (
        <Script
          id={'ga-script'}
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${GA_TRACKING_ID}', {
                    page_path: window.location.pathname,
                  });
                  console.log("GA loaded")
                `,
          }}
        />
      ) : null}
      <Head>
        <title>Business Radar</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <RootWrapper>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <AppContent
            Component={Component}
            pageProps={pageProps}
            router={router}
          />
          <ReactQueryDevtools initialIsOpen={false} />
          <Analytics />
        </HydrationBoundary>
      </RootWrapper>
    </>
  );
};

export default App;
